import { useState, useEffect } from 'react';
import { db, userRoles, eq } from '@/db';
import { useAuth } from '@/providers/AuthProvider';

// HEALTH NOTE (client DB anti-pattern):
// This hook runs direct Drizzle/neon SELECT from browser (useUserRole.ts:21).
// Causes CSP/401/Drizzle errors in some envs (see Codex + audit in RECOVERY_TODAY.md).
// Proper fix: move to server API route + AuthGuard + RLS or edge function.
// Currently non-blocking for nav (falls back to 'standard'); flag for safe refactor before prod scale.

export type UserRole = 'standard' | 'super_user';

export const useUserRole = () => {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const { session } = useAuth();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!session?.user?.id) {
        setUserRole(null);
        setLoading(false);
        return;
      }

      try {
        const result = await db
          .select({ role: userRoles.role })
          .from(userRoles)
          .where(eq(userRoles.userId, session.user.id))
          .limit(1);

        if (result.length === 0) {
          setUserRole('standard'); // Default to standard
        } else {
          setUserRole(result[0].role as UserRole);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole('standard');
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [session?.user?.id]);

  const isSuperUser = userRole === 'super_user';
  const isStandardUser = userRole === 'standard';

  return {
    userRole,
    isSuperUser,
    isStandardUser,
    loading,
  };
};
